// Whether the signed-in account may read the Quote Brain report, and its open
// alerts for the banner (slice LIVE). The server is the only judge: it
// answers 404 to every account but the one named in its platform setting, so
// this hook hides the menu entry and the banner on a 404 and stops asking
// until the account changes. For the reader it re-reads the alerts every five
// minutes.

import { useCallback, useEffect, useRef, useState } from 'react';
import {
    acknowledgeQuoteBrainAlert, fetchQuoteBrainAlerts,
    type QuoteBrainAlert, type QuoteBrainAlertsResponse,
} from '../services/quote-brain-report-api';

const POLL_MS = 5 * 60_000;

export interface QuoteBrainReportAccess {
    visible: boolean;
    /** The server has answered for this account (yes or no). */
    checked: boolean;
    alerts: QuoteBrainAlert[];
    mail: QuoteBrainAlertsResponse['mail'] | null;
    refresh: () => Promise<boolean | null>;
    acknowledge: (id: string) => Promise<void>;
}

export function useQuoteBrainReportAccess(userId: string | null | undefined): QuoteBrainReportAccess {
    const [visible, setVisible] = useState(false);
    const [checked, setChecked] = useState(false);
    const [alerts, setAlerts] = useState<QuoteBrainAlert[]>([]);
    const [mail, setMail] = useState<QuoteBrainAlertsResponse['mail'] | null>(null);
    const userRef = useRef(userId);
    userRef.current = userId;

    // true: this account reads the report; false: it does not (a 404);
    // null: unknown (no account, or the read failed).
    const refresh = useCallback(async (): Promise<boolean | null> => {
        const asked = userRef.current;
        if (!asked) {
            setVisible(false);
            setAlerts([]);
            return null;
        }
        try {
            const result = await fetchQuoteBrainAlerts();
            if (userRef.current !== asked) return null;
            setVisible(Boolean(result));
            setChecked(true);
            setAlerts(result?.alerts || []);
            setMail(result?.mail || null);
            return Boolean(result);
        } catch {
            // A failed read keeps what is on screen; the next poll tries again.
            return null;
        }
    }, []);

    useEffect(() => {
        setVisible(false);
        setChecked(false);
        setAlerts([]);
        if (!userId) return undefined;
        let handle: ReturnType<typeof setInterval> | null = null;
        let cancelled = false;
        (async () => {
            const reads = await refresh();
            // Only the report's reader keeps asking.
            if (cancelled || reads === false) return;
            handle = setInterval(async () => {
                if (userRef.current !== userId) return;
                // A first read that failed left the poll running; a later "no" stops it.
                if ((await refresh()) === false && handle) {
                    clearInterval(handle);
                    handle = null;
                }
            }, POLL_MS);
        })();
        return () => {
            cancelled = true;
            if (handle) clearInterval(handle);
        };
    }, [userId, refresh]);

    const acknowledge = useCallback(async (id: string) => {
        await acknowledgeQuoteBrainAlert(id);
        setAlerts((current) => current.filter((a) => a.id !== id));
    }, []);

    return { visible, checked, alerts, mail, refresh, acknowledge };
}
